import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Zap, ArrowLeft, Eye, EyeOff } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LoginPage() {
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      toast.success(`Welcome back, ${res.data.name || res.data.email}!`);
      if (res.data.role === "admin") {
        navigate("/admin");
      } else {
        navigate("/");
      }
    } catch (error) {
      const msg = error.response?.data?.detail || "Invalid email or password";
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-section-dark flex items-center justify-center py-12 px-4 pt-24 relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 grid-pattern"></div>
      <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-[#38b6ff]/5 rounded-full blur-[150px]"></div>
      <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-[#E8E8E8]/5 rounded-full blur-[120px]"></div>
      
      <motion.div 
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md relative z-10"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/">
            <img
              src="/stenou-logo.png"
              alt="StenoU Logo"
              className="h-20 w-auto mx-auto mb-6"
            />
          </Link>
          <h1 
            className="text-2xl sm:text-3xl font-bold tracking-tight text-[#F5F5F7]"
            style={{ fontFamily: 'Unbounded, sans-serif' }}
            data-testid="login-heading"
          >
            Welcome <span className="gradient-text">Back</span>
          </h1>
          <p className="text-[#B0B0B0] mt-2 text-sm">
            Sign in to access your StenoU account
          </p>
        </div>

        {/* Login Form */}
        <div className="glass-card p-8" data-testid="login-form">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#F5F5F7] text-sm" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                Email
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="rounded-lg bg-[#0D0D0D] border-[#333333] text-[#F5F5F7] placeholder:text-[#E0E0E0]/50 focus:border-[#38b6ff] focus:ring-1 focus:ring-[#38b6ff]"
                required
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-[#F5F5F7] text-sm" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  Password
                </Label>
                <button
                  type="button"
                  className="text-xs text-[#38b6ff] hover:text-[#38b6ff]/80 transition-colors"
                  style={{ fontFamily: 'JetBrains Mono, monospace' }}
                  onClick={() => toast.info("Password reset coming soon!")}
                >
                  Forgot password?
                </button>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="rounded-lg bg-[#0D0D0D] border-[#333333] text-[#F5F5F7] placeholder:text-[#E0E0E0]/50 focus:border-[#38b6ff] focus:ring-1 focus:ring-[#38b6ff] pr-10"
                  required
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#B0B0B0] hover:text-[#F5F5F7] transition-colors"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full rounded-lg bg-[#38b6ff] text-white hover:bg-[#38b6ff]/90 btn-neon font-semibold"
              style={{ fontFamily: 'JetBrains Mono, monospace' }}
              disabled={isLoading}
              data-testid="login-submit-button"
            >
              {isLoading ? (
                "Signing in..."
              ) : (
                <>
                  <Zap className="w-4 h-4 mr-2" />
                  Sign In
                </>
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-[#B0B0B0]">
              Don&apos;t have an account?{" "}
              <a
                href="/#consultation"
                className="text-[#38b6ff] hover:text-[#38b6ff]/80 font-medium transition-colors"
                onClick={(e) => {
                  e.preventDefault();
                  window.location.href = "/stenou#consultation";
                }}
              >
                Book a consultation
              </a>
            </p>
          </div>
        </div>

        {/* Back to home */}
        <div className="text-center mt-6">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-sm text-[#E0E0E0] hover:text-[#38b6ff] transition-colors"
            style={{ fontFamily: 'JetBrains Mono, monospace' }}
          >
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
